import { useState, useEffect, useRef } from "react";
import { useUpdateAvatarMutation } from "../../features/api/userQuery";
import Spinner from "../skeletons/spinnerButton";

function FileSubmitButton({ addToast }) {
  // UseState để quản lý trạng thái mở/đóng của modal
  const [isOpen, setIsOpen] = useState(false);
  // UseState để quản lý animation khi mở/đóng modal
  const [isAnimating, setIsAnimating] = useState(false);
  // UseRef để tham chiếu đến input file
  const fileInputRef = useRef(null);
  // UseState để quản lý file, URL được chọn, Cơ chế lưu: [URL], [File Object]
  const [selectedFile, setSelectedFile] = useState(null);

  const [updateAvatar, { isLoading }] = useUpdateAvatarMutation();

  useEffect(() => {
    if (isOpen) setIsAnimating(true);
  }, [isOpen]);

  // Xử lí điều kiện của file
  const handleFile = (file) => {
    // Kiểm tra định dạng file
    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      addToast(
        "Vui lòng chọn tệp ảnh hợp lệ (JPEG, PNG, GIF)",
        "error",
        "warning",
      );
      fileInputRef.current.value = null;
      return;
    }
    // Kiểm tra kích thước file (ví dụ: không quá 1MB)
    const maxSize = 1 * 1024 * 1024;
    if (file.size > maxSize) {
      addToast(
        "Kích thước tệp quá lớn. Vui lòng chọn tệp nhỏ hơn 1MB.",
        "error",
        "warning",
      );
      fileInputRef.current.value = null;
      return;
    }
    // Xử lí tệp hợp lệ (ví dụ: tải lên server hoặc hiển thị ảnh)
    setSelectedFile(file);
  };

  // 1. Xử lí Input File khi người dùng chọn tệp
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  // 2. Xử lí Drag and Drop
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileInputClick = () => {
    fileInputRef.current.click();
  };

  // 3. Xử lí Submit - Ưu tiên Url
  const handleSubmit = async () => {
    try {
      const data = await updateAvatar(selectedFile).unwrap();
      if (data) {
        addToast("Cập nhật ảnh đại diện thành công!", "success", "check");
        setSelectedFile(null);
      }
    } catch (error) {
      console.error("Error updating avatar:", error);
    } finally {
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        className={`flex items-center justify-center bg-white w-auto h-10 text-black px-4
        rounded-sm  border border-lessgrayColor text-sm font-normal`}
        onClick={() => setIsOpen(true)}
      >
        Thay đổi ảnh đại diện
      </button>
      {/* Modal */}
      {isAnimating && (
        <div className="fixed inset-0 z-[999]">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black bg-opacity-60 z-10"
            onClick={() => setIsOpen(false)}
          ></div>
          <div
            className={`absolute top-1/2 left-1/2 md:max-w-[600px] w-[96%] md:w-full h-auto
                    flex flex-col items-start justify-start bg-white rounded-lg py-4 px-8 select-none z-20
                    ${isOpen ? "animate-zoomInAbsolute" : "animate-zoomOutAbsolute"}`}
            onAnimationEnd={() => {
              if (!isOpen) setIsAnimating(false);
            }}
          >
            <div className="w-full border-b border-lesslessgrayColor pb-4">
              <div className="text-xl text-black font-semibold">
                Tải ảnh đại diện
              </div>
              <div className="text-base text-moregrayTextColor font-medium">
                Nhập Url hoặc tải ảnh bạn thích từ thiết bị của mình
              </div>
            </div>
            {/* Nơi Drag và Drop hoặc Chọn tệp bằng Button */}
            <div
              className="relative border-2 border-dashed border-lessgrayColor bg-backgroundLightGrayColor
               rounded-md w-full h-60 mt-4
            flex flex-col items-center justify-center gap-1 cursor-pointer"
              onClick={handleFileInputClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="text-lg font-medium">Kéo & Thả Tệp Ảnh</div>
              <div className="">hoặc</div>
              <label
                className="bg-blueButton text-white px-6 py-2 rounded-md cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                Chọn tệp
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </label>
            </div>
            <div className="w-full border-b border-lesslessgrayColor my-8"></div>
            <div className="w-full flex items-center justify-between mb-2 gap-2">
              <div className="text-blueButton flex-1 overflow-hidden whitespace-nowrap text-ellipsis">
                {selectedFile ? (
                  <>
                    <span className="text-black">Đã chọn: </span>
                    {selectedFile.name}
                  </>
                ) : (
                  <span></span>
                )}
              </div>
              <div className="flex items-center justify-end gap-4">
                <button
                  className="relative bg-blueButton text-white px-6 py-2 rounded-md cursor-pointer"
                  onClick={() => handleSubmit()}
                >
                  Tải lên
                  <div
                    className={`absolute top-0 left-0 w-full h-full flex items-center justify-center
                                    ${isLoading ? "bg-white/50" : "hidden"}`}
                  >
                    <Spinner
                      size={"30px"}
                      stroke={"5px"}
                      _hidden={isLoading ? "" : "hidden"}
                      color={"white"}
                    />
                  </div>
                </button>
                <button
                  className="bg-backgroundGrayColor text-black border border-black rounded-md px-6 h-10
                                border-lessgrayColor text-md font-normal"
                  onClick={() => setIsOpen(false)}
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
export default FileSubmitButton;
